import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Clock, Users, MapPin, Sparkles, MessageCircle } from "lucide-react";
import { MaskedLines, Reveal, EASE } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES, waLink } from "@/lib/data";

const CATS = [
  { key: "alles", label: "Alles" },
  { key: "goochelen", label: "Goochelen" },
  { key: "circus", label: "Circus" },
  { key: "workshops", label: "Workshops" },
];
const DOEL = [
  { key: "alles", label: "Alles" },
  { key: "zakelijk", label: "Zakelijk" },
  { key: "particulier", label: "Privé" },
];

export default function Aanbod() {
  const [cat, setCat] = useState("alles");
  const [doel, setDoel] = useState("alles");

  const shown = SERVICES.filter((s) => cat === "alles" || s.categories.includes(cat));

  return (
    <>
      <Seo
        title="Alle shows & workshops | entertainmentshows.be"
        description="Van podiumgoochelen en close-up magie tot circusacts en hands-on workshops: ontdek het volledige aanbod en filter op jouw feest."
        path="/aanbod"
      />
      <section className="relative overflow-hidden pb-16 pt-40" data-testid="aanbod-hero">
        <div className="brand-glow pointer-events-none absolute inset-0" />
        <div className="confetti-dots pointer-events-none absolute inset-0 opacity-70" />
        <div className="relative mx-auto max-w-7xl px-6">
          <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.35em] text-gold-light">
            <Sparkles size={14} />
            Het volledige aanbod
          </p>
          <MaskedLines
            as="h1"
            lines={["Wat komt er", <>uit de <em className="gold-text italic">hoed</em>?</>]}
            className="mt-5 block font-display text-4xl font-black leading-[1.05] tracking-tight text-snow sm:text-5xl lg:text-6xl"
          />
          <Reveal delay={0.3} y={16}>
            <p className="mt-6 max-w-xl text-lg font-semibold leading-relaxed text-blush">
              Goochelshows, circuskunst en workshops voor kinderfeesten, huwelijken en elk ander
              feest. Filter op discipline of doelgroep en vind jouw perfecte show!
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 lg:py-24" data-testid="aanbod-catalog">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal y={14} className="flex flex-wrap items-center gap-6">
            <div className="flex flex-wrap gap-2" data-testid="filter-categorie">
              {CATS.map((c) => (
                <button
                  key={c.key}
                  data-testid={`filter-button-${c.key}`}
                  onClick={() => setCat(c.key)}
                  className={`joy-hover rounded-full border-2 px-5 py-2.5 text-sm font-extrabold ${
                    cat === c.key
                      ? "border-brand bg-brand text-white"
                      : "border-brand/30 text-brand hover:border-brand"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="filter-doelgroep">
              {DOEL.map((d) => (
                <button
                  key={d.key}
                  data-testid={`filter-doel-${d.key}`}
                  onClick={() => setDoel(d.key)}
                  className={`joy-hover rounded-full px-4 py-2.5 text-sm font-extrabold transition-colors duration-300 ${
                    doel === d.key ? "bg-gold/30 text-brand-dark" : "text-rose hover:text-brand"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-12 grid gap-5 md:grid-cols-2">
            <AnimatePresence mode="popLayout">
              {shown.map((s) => (
                <motion.article
                  layout
                  key={s.slug}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="card-glow group relative flex h-full flex-col overflow-hidden rounded-3xl border-2 border-gold/50 bg-white"
                  data-testid={`catalog-card-${s.slug}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="h-full w-full object-cover opacity-85 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/70 via-transparent to-transparent" />
                    <span className="absolute left-5 top-5 rounded-full bg-gold px-4 py-1.5 text-xs font-black uppercase tracking-[0.2em] text-brand-dark">
                      {s.tag}
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-display text-2xl font-black text-cocoa">{s.title}</h3>
                    <p className="mt-2 flex-1 font-semibold leading-relaxed text-rose">{s.tagline}</p>

                    <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs font-bold text-rose">
                      <span className="flex items-center gap-2">
                        <Clock size={13} className="text-gold-deep" /> {s.duration}
                      </span>
                      <span className="flex items-center gap-2">
                        <Users size={13} className="text-gold-deep" /> {s.audience}
                      </span>
                      <span className="flex items-center gap-2">
                        <MapPin size={13} className="text-gold-deep" /> {s.location}
                      </span>
                    </div>

                    <div className="mt-7 flex flex-wrap gap-3">
                      <Link
                        to={`/contact?service=${s.slug}`}
                        data-testid={`catalog-offerte-${s.slug}`}
                        className="joy-hover group/btn flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-black uppercase tracking-wider text-brand-dark"
                      >
                        Plan jouw feestje!
                        <ArrowRight size={14} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
                      </Link>
                      <a
                        href={waLink(`Hallo entertainmentshows.be! Ik wil graag info over: ${s.title}.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`catalog-whatsapp-${s.slug}`}
                        aria-label={`WhatsApp over ${s.title}`}
                        className="joy-hover flex h-11 w-11 items-center justify-center rounded-full border-2 border-whatsapp/60 text-whatsapp hover:bg-whatsapp hover:text-white"
                      >
                        <MessageCircle size={16} />
                      </a>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>

          <Reveal className="mt-10">
            <div className="card-glow flex flex-wrap items-center justify-between gap-6 rounded-3xl border-2 border-gold/60 bg-white p-8" data-testid="aanbod-opmaat-card">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-gold-deep">Iets anders in gedachten?</p>
                <h3 className="mt-2 font-display text-2xl font-black text-cocoa">Op maat gemaakt concept</h3>
                <p className="mt-2 max-w-md font-semibold text-rose">
                  Van walking acts tot een avondvullende show: vertel ons je idee en wij toveren het!
                </p>
              </div>
              <Link
                to="/contact?service=op-maat"
                data-testid="aanbod-opmaat-cta"
                className="joy-hover flex items-center gap-2 rounded-full border-2 border-brand/40 px-7 py-3.5 text-sm font-extrabold uppercase tracking-wider text-brand hover:bg-brand hover:text-white"
              >
                Vertel ons je idee!
                <ArrowUpRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
